import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { AuthImage } from "@/components/AuthImage";
import { TodayWidget } from "@/components/TodayWidget";
import { ShopOnboardingChecklist } from "@/components/ShopOnboardingChecklist";
import { PullToRefresh } from "@/components/PullToRefresh";
import { Star, DollarSign, Clock, CheckCircle2, MapPin, Lock, Crown, Gift } from "lucide-react";

export default function ShopDashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const browseMode = location.pathname.startsWith("/browse");
  const [openIssues, setOpenIssues] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadIssues = useCallback(async () => {
    const a = await api.get("/issues");
    setOpenIssues(a.data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadIssues();
      } finally { setLoading(false); }
    })();
  }, [loadIssues]);

  const noLocation = user && !user.location?.lat;
  const tier = user?.subscription_tier || "free";
  const isPaid = tier === "pro" || tier === "premium";
  const isPremium = tier === "premium";
  const trialUntil = user?.welcome_trial_until;
  const premiumUntil = user?.premium_until;
  const isOnTrial = !!(isPaid && trialUntil && premiumUntil && new Date(premiumUntil).getTime() <= new Date(trialUntil).getTime() + 60_000);
  const trialDaysLeft = isOnTrial && trialUntil ? Math.max(0, Math.ceil((new Date(trialUntil).getTime() - Date.now()) / 86_400_000)) : 0;
  const trialEligible = !!user?.trial_eligible;
  const lockedCount = openIssues.filter((i) => i.locked).length;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <PullToRefresh onRefresh={loadIssues}>
        <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid={browseMode ? "browse-jobs-page" : "shop-dashboard"}>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="label-eyebrow mb-3">{browseMode ? "JOB FEED" : "SHOP CONTROL ROOM"}</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">{browseMode ? "All nearby jobs." : "Live jobs."}</h1>
            <p className="text-zinc-400 mt-2">{browseMode ? "Filter by what fits your bays." : "Quote privately. Win publicly."}</p>
          </div>
          {browseMode ? (
            <Link to="/shop" className="btn-ghost" data-testid="cta-back-dashboard">← Back to dashboard</Link>
          ) : (
            <Link to="/browse" className="btn-primary" data-testid="cta-browse-all">Browse All Jobs</Link>
          )}
        </div>

        {!browseMode && <TodayWidget />}

        {!browseMode && <ShopOnboardingChecklist />}

        {noLocation && (
          <div className="border border-[#FF9500]/40 bg-[#FF9500]/5 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="shop-no-location-banner">
            <MapPin className="w-4 h-4 text-[#FF9500]" />
            <div className="flex-1 text-sm text-[#FF9500]">Set your shop location to start receiving nearby jobs from owners using radius search.</div>
            <Link to="/settings" className="btn-secondary text-xs py-1.5">Set location</Link>
          </div>
        )}

        {isOnTrial && !browseMode && (
          <div className="border border-[#FF9500]/40 bg-gradient-to-r from-[#FF9500]/15 to-[#FF9500]/0 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="trial-active-banner">
            <Gift className="w-4 h-4 text-[#FF9500] shrink-0" />
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">Free {tier === "premium" ? "Premium" : "Pro"} trial — {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left.</span>
              <span className="text-zinc-300"> {tier === "premium" ? "Every nearby job is unlocked" : "3 concurrent jobs unlocked"} until {new Date(premiumUntil).toLocaleDateString()}.</span>
            </div>
            <Link to="/upgrade" className="btn-primary text-xs py-1.5" data-testid="trial-upgrade-cta">Manage plan</Link>
          </div>
        )}

        {trialEligible && !browseMode && (
          <div className="border border-[#FF9500]/40 bg-gradient-to-r from-[#FF9500]/20 to-[#FF9500]/0 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="trial-eligible-banner">
            <Gift className="w-5 h-5 text-[#FF9500] shrink-0 animate-pulse" />
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">Claim your 30-day free trial.</span>
              <span className="text-zinc-300"> Pick Pro ($59.99/mo) or Premium ($79.99/mo) — no charge for 30 days · cancel anytime.</span>
            </div>
            <Link to="/upgrade" className="btn-primary text-xs py-1.5" data-testid="claim-trial-cta">Start free trial</Link>
          </div>
        )}

        {!isPremium && lockedCount > 0 && (
          <div className="border border-[#FF3B30]/40 bg-gradient-to-r from-[#FF3B30]/10 to-[#FF3B30]/0 px-5 py-4 mb-6 flex flex-wrap items-center gap-3" data-testid="upgrade-banner">
            <Crown className="w-4 h-4 text-[#FF3B30]" />
            <div className="flex-1 text-sm">
              <span className="text-white font-semibold">{lockedCount} more job{lockedCount !== 1 ? "s" : ""} nearby</span>
              <span className="text-zinc-400"> — unlock all and message any owner with Premium.</span>
            </div>
            <Link to="/upgrade" className="btn-primary text-xs py-1.5" data-testid="upgrade-cta-banner">Upgrade</Link>
          </div>
        )}

        <h2 className="font-heading text-2xl font-bold tracking-tight mb-6">Available Jobs</h2>

        {loading ? (
          <div className="text-zinc-500">Loading...</div>
        ) : openIssues.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center text-zinc-500">No open jobs right now.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {openIssues.map((i) => {
              const isLocked = !!i.locked;
              const Card = (
                <div className={`aspect-[4/3] bg-zinc-900 relative ${isLocked ? "select-none" : ""}`}>
                  {!isLocked && i.file_paths?.[0] ? (
                    <AuthImage path={i.file_paths[0]} className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center text-zinc-700 text-xs uppercase tracking-widest ${isLocked ? "blur-sm" : ""}`}>
                      {isLocked ? "PREMIUM ONLY" : "No Media"}
                    </div>
                  )}
                  {i.ai_category && (
                    <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest bg-[#007AFF] text-white px-2 py-1">{i.ai_category}</span>
                  )}
                  {i.distance_miles != null && (
                    <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest bg-black/70 backdrop-blur text-white px-2 py-1 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {i.distance_miles} mi</span>
                  )}
                  {isLocked && (
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-2" data-testid={`locked-overlay-${i.issue_id}`}>
                      <Lock className="w-6 h-6 text-[#FF3B30]" strokeWidth={1.5} />
                      <div className="text-[10px] uppercase tracking-widest text-white">Premium Only</div>
                    </div>
                  )}
                </div>
              );
              const Body = (
                <div className={`p-4 ${isLocked ? "opacity-60" : ""}`}>
                  <div className="label-eyebrow text-zinc-500 mb-1.5">{isLocked ? "—" : (i.vehicle_make || "?")} · {isLocked ? "—" : (i.vehicle_year || "—")}</div>
                  <h3 className="font-heading text-sm font-bold mb-1.5 line-clamp-1">{i.title}</h3>
                  <p className="text-xs text-zinc-500 line-clamp-2">{isLocked ? "Upgrade to Premium to view this job's details." : i.description}</p>
                </div>
              );
              if (isLocked) {
                return (
                  <Link key={i.issue_id} to="/upgrade" className="card-interactive p-0 overflow-hidden block relative" data-testid={`job-card-${i.issue_id}`}>
                    {Card}{Body}
                  </Link>
                );
              }
              return (
                <Link key={i.issue_id} to={`/issue/${i.issue_id}`} className="card-interactive p-0 overflow-hidden block relative" data-testid={`job-card-${i.issue_id}`}>
                  {Card}{Body}
                </Link>
              );
            })}
          </div>
        )}
        </main>
      </PullToRefresh>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-[#09090B] p-6">
      <div className="label-eyebrow mb-3">{label}</div>
      <div className={`font-heading text-4xl font-black ${accent ? "text-[#FF3B30]" : ""}`}>{value}</div>
    </div>
  );
}
